import { loader } from 'graphql.macro';
import styles from './InspectionItemComments.module.scss';
import { Text } from '../../typography';
import { useTranslation } from 'react-i18next';
import { keys } from '../../../utilities/translator/translation_keys';
import { Form } from 'react-bootstrap';
import { useState, useRef, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { useCurrentUser } from '../../../providers/UserProvider';
import { useParams } from 'react-router-dom';
import { getLocalTime } from '../../../utilities/time';
import QuickFileDropzone from '../../image_uploads/QuickFileDropzone';
import findFileIcon, { imageExtensions } from '../../../utilities/files';
import { openFileViewer } from '../../../graphql/cache/modal';
import { downloadFile } from '../../../utilities/inspection';
import { useIsMobile } from '../../../hooks/misc';
import { Icon } from '@mui/material';
import { customColors } from '../../../utilities';
import { useModal } from '../../../providers/ModalProvider';

const addInspectionItemCommentMutation = loader(
  './InspectionItemComments.addComment.graphql',
);
const updateCommentMutation = loader('./InspectionItemComments.update.graphql');
const deleteCommentMutation = loader('./InspectionItemComments.delete.graphql');

export default function InspectionItemComments({
  comments,
  itemId,
  refetch = () => {},
}) {
  const { openConfirmationModal } = useModal();
  const { id } = useParams();
  const [text, setText] = useState('');
  const { t } = useTranslation();
  const [addComment] = useMutation(addInspectionItemCommentMutation);
  const [updateComment] = useMutation(updateCommentMutation);
  const [deleteComment] = useMutation(deleteCommentMutation);
  const { user } = useCurrentUser();
  const ref = useRef(null);
  const commentRef = useRef(null);

  useEffect(() => {
    if (ref.current) {
      ref.current.scrollTo(0, 0);
    }
  }, [comments]);
  const isMobile = useIsMobile();

  const [isEditing, setIsEditing] = useState(false);

  return (
    <div className={styles.container}>
      <div className={styles.input}>
        <img
          className={styles.userAvatar}
          src={user?.avatarUrl}
          alt="your avatar"
        />
        <Form.Control
          value={text}
          as={'textarea'}
          onChange={(e) => setText(e.target.value)}
          placeholder={t(keys.action_items.COMMENT)}
        />
        <QuickFileDropzone
          icon={true}
          iconText=""
          iconImage={'attachment'}
          iconSize="1.5rem"
          onSubmit={({ url, name }) =>
            addComment({
              variables: {
                text: name,
                attachmentUrl: url,
                itemId,
                inspectionId: parseInt(id),
              },
            }).then(() => refetch())
          }
        />
        <Icon
          onClick={() => {
            if (text) {
              addComment({
                variables: { text, itemId, inspectionId: parseInt(id) },
              }).then(() => {
                refetch();
                setText('');
              });
            }
          }}
          disabled={!text}
          sx={{
            fontSize: isMobile ? '1rem' : undefined,
            color: !!text ? customColors.BLUE : customColors.SECONDARY_LIGHT,
            cursor: !!text ? 'pointer' : 'auto',
          }}
        >
          send
        </Icon>
      </div>
      <div key={id} className={styles.commentsContainer} ref={ref}>
        {comments?.map(
          (
            { creator, id, text, dateCreated, dateModified, attachmentUrl },
            idx,
          ) => {
            const extension = attachmentUrl?.split('.').pop();
            const isImage = imageExtensions.includes(extension);
            const file = {
              name: text,
              fileType: extension,
              downloadAllowed: true,
              url: attachmentUrl,
            };

            const isCreator = creator?.id === user?.id;
            const wasEdited = dateCreated !== dateModified;

            return (
              <div
                key={id}
                className={
                  idx % 2 === 0
                    ? styles.commentContainer
                    : styles.commentContainerOdd
                }
              >
                <img
                  className={styles.userAvatar}
                  src={creator?.avatarUrl}
                  alt="your avatar"
                />
                <div className={styles.commentContent}>
                  <div className={styles.header}>
                    <div className={styles.flexRow}>
                      <Text
                        size="md"
                        weight="semiBold"
                        noMargin
                      >{`${creator?.firstName} ${creator?.lastName}`}</Text>
                      <Text size="sm" noMargin color="secondary">
                        {`${getLocalTime(dateCreated).format(
                          'dddd MMMM Do YYYY HH:mm',
                        )} ${
                          wasEdited
                            ? `(${t(keys.common.EDITED).toLowerCase()})`
                            : ''
                        }`}
                      </Text>
                    </div>
                    {isCreator && (
                      <div>
                        <Icon
                          className={styles.icon}
                          sx={{
                            fontSize: '1rem',
                            color: isEditing ? 'green' : null,
                          }}
                          onClick={() =>
                            setIsEditing((prev) => (prev === id ? null : id))
                          }
                          baseClassName="material-icons-outlined"
                        >
                          {isEditing === id ? 'check' : 'edit'}
                        </Icon>
                        <Icon
                          className={styles.icon}
                          sx={{ fontSize: '1rem', color: 'red' }}
                          onClick={() =>
                            openConfirmationModal({
                              variant: 'warning',
                              title: t(keys.action.DELETE_VARIABLE, {
                                variable: t(
                                  keys.common.THIS_COMMENT,
                                ).toLowerCase(),
                              }),
                              description: t(keys.action.DELETE_CONFIRMATION, {
                                variable: t(
                                  keys.common.THIS_COMMENT,
                                ).toLowerCase(),
                              }),
                              onSubmit: () =>
                                deleteComment({
                                  variables: {
                                    id,
                                  },
                                }).then(() => refetch()),
                            })
                          }
                        >
                          delete_outlined
                        </Icon>
                      </div>
                    )}
                  </div>
                  <div className={styles.commentText}>
                    {!!attachmentUrl ? (
                      <div>
                        <img
                          className={styles.attachment}
                          src={
                            isImage ? attachmentUrl : findFileIcon(extension)
                          }
                          alt="attachment"
                          onClick={() => openFileViewer(file)}
                        />
                        <Text
                          onClick={() => downloadFile(attachmentUrl, text)}
                          noMargin
                          size="sm"
                          color="accentPrimary"
                          hover
                        >
                          {text}
                        </Text>
                      </div>
                    ) : isEditing === id ? (
                      <Form.Control
                        as="textarea"
                        rows={2}
                        ref={commentRef}
                        defaultValue={text}
                        onBlur={(e) =>
                          updateComment({
                            variables: {
                              id,
                              text: e.target.value,
                            },
                          })
                        }
                      />
                    ) : (
                      <Text size="md" prewrap noMargin>
                        {text}
                      </Text>
                    )}
                  </div>
                </div>
              </div>
            );
          },
        )}
      </div>
    </div>
  );
}
